import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { MethodSetupResponse, RegistrationResponse } from "../../../types";
import { StripeError } from "@stripe/stripe-js";
import { OnApproveBraintreeData } from "@paypal/react-paypal-js/dist/types/types/braintreePayPalButtonTypes";
import { BraintreeTokenizePayload } from "@paypal/react-paypal-js/dist/types/types/braintree/commonsTypes";
import { ValidationRoutingNumberResponse } from "@ftdr/cscpayments-js-client/lib/types/types";

type PaypalData = {
  onApproveBraintreeData?: OnApproveBraintreeData;
  braintreeTokenizePayloadData?: BraintreeTokenizePayload;
};

export type PaymentsClientState = {
  isLoading?: boolean;
  clientSetup: RegistrationResponse;
  paymentMethodSetup: MethodSetupResponse;
  error: {
    isError?: boolean;
    message?: string;
    stripeError?: Partial<StripeError>;
  };
  isComplete?: boolean;
  paymentUuid: {
    traceId: string;
    spanId: string;
  };
  paypalData?: PaypalData;
  paypalError?: {
    isError?: boolean;
    message?: string;
  };
  isPaypalApproved?: boolean;
  validationNumberResponseState?: ValidationRoutingNumberResponse;
  isPending?: boolean;
};

export const initialState: PaymentsClientState = {
  clientSetup: {},
  paymentMethodSetup: {},
  isLoading: true,
  error: { isError: false, message: "", stripeError: {} },
  isComplete: false,
  paymentUuid: {
    traceId: "",
    spanId: "",
  },
  isPaypalApproved: false,
  isPending: false,
};

export const paymentsClientSetupSlice = createSlice({
  name: "paymentsClientSetupState",
  initialState,
  reducers: {
    setPaymentsClientState(
      state,
      { payload }: PayloadAction<RegistrationResponse | undefined>
    ) {
      state.error = { message: "", isError: false };
      state.clientSetup = payload || initialState.clientSetup;
    },
    setIsLoading(state, { payload }: PayloadAction<boolean>) {
      state.isLoading = payload;
    },
    setIsComplete(state, { payload }: PayloadAction<boolean>) {
      state.isComplete = payload;
    },
    setPaymentsMethodState(
      state,
      { payload }: PayloadAction<MethodSetupResponse | undefined>
    ) {
      state.error = { message: "", isError: false };
      state.paymentMethodSetup = payload || initialState.paymentMethodSetup;
    },
    setError(
      state,
      {
        payload,
      }: PayloadAction<{
        isError?: boolean;
        message?: string;
        stripeError?: Partial<StripeError>;
      }>
    ) {
      state.error = payload;
    },
    setPaymentUuid(
      state,
      {
        payload,
      }: PayloadAction<{
        traceId: string;
        spanId: string;
      }>
    ) {
      state.paymentUuid = payload;
    },
    setPaypalData(state, { payload }: PayloadAction<PaypalData>) {
      state.paypalData = payload;
    },
    setPaypalError(
      state,
      {
        payload,
      }: PayloadAction<{
        isError?: boolean;
        message?: string;
      }>
    ) {
      state.paypalError = payload;
    },
    setIsPaypalApproved(state, { payload }: PayloadAction<boolean>) {
      state.isPaypalApproved = payload;
    },
    setValidationNumberResponseState(
      state,
      { payload }: PayloadAction<ValidationRoutingNumberResponse | undefined>
    ) {
      state.error = { message: "", isError: false };
      state.validationNumberResponseState =
        payload || initialState.validationNumberResponseState;
    },
    setIsPending(state, { payload }: PayloadAction<boolean>) {
      state.isPending = payload;
    },
  },
});

export const {
  setError,
  setPaymentsClientState,
  setPaymentsMethodState,
  setIsLoading,
  setIsComplete,
  setPaymentUuid,
  setPaypalData,
  setIsPaypalApproved,
  setPaypalError,
  setValidationNumberResponseState,
  setIsPending,
} = paymentsClientSetupSlice.actions;
